import React, { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Check, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM_STATE = {
  contact_name: '',
  business_name: '',
  email: '',
  phone_number: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * useLeadForm — state + validation + submit hook for the lead-capture form.
 * Kept in this file since it is only used by this single form.
 */
function useLeadForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const validate = useCallback(() => {
    const missing = Object.values(formData).some((v) => !v.trim());
    if (missing) {
      toast.error('Please fill in all fields');
      return false;
    }
    if (!EMAIL_REGEX.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    return true;
  }, [formData]);

  const submit = useCallback(async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit');
      }
      setIsSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
      if (typeof window !== 'undefined' && typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validate]);

  return { isSubmitting, isSubmitted, formData, updateField, submit };
}

/**
 * LeadCaptureForm — single-step lead form embedded in the landing hero.
 *
 * Collects the minimum information required to schedule a follow-up
 * conversation with a merchant: name, business name, email, phone.
 * Additional details (address, city, etc.) are captured during the
 * follow-up rather than being asked upfront.
 */
export default function LeadCaptureForm() {
  const { isSubmitting, isSubmitted, formData, updateField, submit } = useLeadForm();

  const handleSubmit = (e) => {
    e.preventDefault();
    submit();
  };

  if (isSubmitted) {
    return <SuccessCard />;
  }

  return (
    <div
      data-testid="lead-capture-form"
      className="relative bg-card/95 backdrop-blur-sm border border-border p-6 md:p-8 shadow-[0_0_60px_-12px_hsl(var(--primary)/0.35)]"
    >
      <span aria-hidden className="absolute top-0 left-0 h-1 w-16 bg-primary" />
      <h2 className="text-xl md:text-2xl font-heading font-bold tracking-tight text-foreground mb-1.5">
        Tell us about your business
      </h2>
      <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
        Complete this short form and we&apos;ll contact you to learn more about your
        business, answer your questions, and determine whether Hungry Canadian
        is the right fit for your operation.
      </p>

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <Field
          id="contact_name"
          testId="input-contact-name"
          label="Your Name"
          placeholder="First and last name"
          value={formData.contact_name}
          onChange={(v) => updateField('contact_name', v)}
          autoFocus
          autoComplete="name"
        />
        <Field
          id="business_name"
          testId="input-business-name"
          label="Business Name"
          placeholder="Your restaurant, café, or shop"
          value={formData.business_name}
          onChange={(v) => updateField('business_name', v)}
          autoComplete="organization"
        />
        <Field
          id="email"
          testId="input-email"
          type="email"
          inputMode="email"
          label="Email Address"
          placeholder="you@yourbusiness.com"
          value={formData.email}
          onChange={(v) => updateField('email', v)}
          autoComplete="email"
        />
        <Field
          id="phone_number"
          testId="input-phone-number"
          type="tel"
          inputMode="tel"
          label="Phone Number"
          placeholder="(403) 555-1234"
          value={formData.phone_number}
          onChange={(v) => updateField('phone_number', v)}
          autoComplete="tel"
        />

        <Button
          type="submit"
          data-testid="submit-btn"
          disabled={isSubmitting}
          className="w-full h-12 text-base font-semibold rounded-full bg-primary hover:bg-primary-hover text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </form>

      <p className="text-xs text-muted-foreground text-center mt-5 leading-relaxed">
        No obligation. No pressure. Just a conversation about your business and
        your goals.
      </p>
    </div>
  );
}

function SuccessCard() {
  return (
    <div
      data-testid="lead-form-success"
      className="relative bg-card border border-border p-7 md:p-8 text-center shadow-[0_0_60px_-12px_hsl(var(--primary)/0.35)]"
    >
      <div className="w-12 h-12 bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-4">
        <Check className="w-5 h-5 text-primary" />
      </div>
      <h3 className="text-xl font-heading font-bold tracking-tight text-foreground mb-2">
        Thanks — we&apos;ll be in touch
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed">
        We&apos;ve received your details and will reach out within one business day
        to learn more about your business.
      </p>
    </div>
  );
}

function Field({
  id,
  testId,
  label,
  type = 'text',
  inputMode,
  placeholder,
  value,
  onChange,
  autoFocus,
  autoComplete,
}) {
  return (
    <div>
      <Label
        htmlFor={id}
        className="text-sm text-foreground font-medium mb-1.5 block"
      >
        {label}
      </Label>
      <Input
        id={id}
        name={id}
        data-testid={testId}
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        className="h-12 bg-background border-border text-foreground placeholder:text-muted-foreground/40 focus:border-primary/50 focus:ring-ring/20 rounded-none"
      />
    </div>
  );
}
